import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { css } from '@emotion/react'
import { isMobile } from 'react-device-detect'
import { memo } from 'react'

import { useRouter } from 'next/router'

import Link from '@/components/common/Link'
import BurgerWhite from '@/components/layouts/Results/Burger'

import { CustomizedClientsResponse } from '@/service'

import { useMarket } from '@/utils/multi-markets/context'
import { getNavigationIcon } from '@/constants/navigationAssets'

import Links from '../navigation/Links'

import {
  callUsContainer,
  callUsNumber,
  callUsTitle,
  iconContainer,
  iconLink,
  linksList,
  linksListItem,
  onlyDesktop
} from '../navigation/Links.styled'

import NavigationLinks from './NaviagtionLinks'
import { useNavbar } from './useNavbar'
import {
  Container,
  NavigationContainer,
  LinksListWrapper,
  LogoWrapper,
  BurgerWrapper,
  Breadcrumb,
  OpenedNavbar
} from './Navbar.styled'

const Burger = dynamic(() => import('@/components/navigation/Burger'), {
  ssr: false,
  loading: () => <div style={{ width: 24, height: 24 }} />
})

interface Props {
  short?: boolean
  highlight?: boolean
  breadcrumbsText?: string
  clientDetails?: CustomizedClientsResponse
}

function Navbar(props: Props) {
  const { t } = useTranslation('common')
  const { market } = useMarket()
  const router = useRouter()
  const {
    short = false,
    highlight = false,
    breadcrumbsText = '',
    clientDetails
  } = props

  const {
    open,
    scrolled,
    hydrated,
    isParkingSearch,
    getLogoConfig,
    handleToggle,
    handleClose,
    setOpen
  } = useNavbar({ short, highlight, clientDetails })

  if (!hydrated) {
    return (
      <div
        style={{
          height: short ? 60 : 80,
          width: '100%',
          position: 'relative',
          zIndex: 99,
          backgroundColor: 'white',
          contain: 'layout'
        }}
        aria-hidden="true"
      />
    )
  }

  const closeMenu = () => setOpen(false)

  const renderLogo = () => {
    const { href, imgSrc, isNarrow, dataTestId, LogoComponent } =
      getLogoConfig()

    const logoContent = imgSrc ? (
      <img
        src={imgSrc}
        css={logoImgStyle}
        alt={t('a11y.logo' as keyof typeof t)}
        width={isNarrow ? 132 : 160}
        height={30}
      />
    ) : (
      LogoComponent
    )

    const wrapperProps = {
      'onClick': closeMenu,
      'aria-label': t('a11y.pageName'),
      ...(isNarrow && { narrow: true }),
      ...(dataTestId && { 'data-test-id': dataTestId })
    }

    if (!href) {
      return (
        <LogoWrapper as="a" {...wrapperProps} onClick={() => router.push('/')}>
          {logoContent}
        </LogoWrapper>
      )
    }

    return (
      <Link href={href} passHref prefetch={false}>
        <LogoWrapper {...wrapperProps}>{logoContent}</LogoWrapper>
      </Link>
    )
  }

  const renderBurger = () => (
    <BurgerWrapper
      onClick={short ? handleClose : handleToggle}
      backgroundColor={
        open ? '#3b8ded' : isParkingSearch && isMobile ? '#093769' : '#f2f6fd'
      }
      scrolled={scrolled}
    >
      {isParkingSearch && isMobile && !open ? (
        <BurgerWhite short={short} open={open} />
      ) : (
        <Burger short={short} open={open} />
      )}
    </BurgerWrapper>
  )

  return (
    <Container
      id="handler"
      scrolled={scrolled || short || highlight}
      isDark={isParkingSearch && isMobile}
      backgroundColor={isParkingSearch && isMobile && open && '#3b8ded'}
      isMobile={isMobile}
    >
      <NavigationContainer scrolled={scrolled}>
        <OpenedNavbar
          scrolled={scrolled}
          isMobile={isMobile}
          backgroundColor={
            open
              ? '#3b8ded'
              : isParkingSearch && isMobile
              ? '#093769'
              : '#f2f6fd'
          }
        >
          {renderLogo()}
        </OpenedNavbar>
        {short && breadcrumbsText && (
          <Breadcrumb>| {breadcrumbsText}</Breadcrumb>
        )}
        {renderBurger()}
        <LinksListWrapper open={open}>
          {isMobile ? (
            <NavigationLinks onClose={closeMenu} />
          ) : (
            <Links short={short} onClick={closeMenu} />
          )}
        </LinksListWrapper>
        {!isMobile && (
          <ul css={linksList}>
            <li css={[linksListItem, onlyDesktop]} data-test-id="menu-call-us">
              <div css={callUsContainer}>
                <span css={callUsTitle}>{t('navigation.callUs.title')}</span>
                <div css={callUsNumber}>
                  {market === 'pl'
                    ? t('navigation.callUs.phone')
                    : market === 'ae'
                    ? t('navigation.callUs.phoneUAE')
                    : t('navigation.callUs.phoneBH')}
                  {market === 'ae' && (
                    <>
                      <div css={iconContainer}>
                        <a
                          href={t(`socials.${market}.phoneDial`)}
                          target="_blank"
                          css={iconLink}
                        >
                          <img
                            src={getNavigationIcon('phoneRinging')}
                            alt="phone"
                            loading="lazy"
                          />
                        </a>
                      </div>
                      <div css={iconContainer}>
                        <a
                          href={t('socials.whatsAppUAE')}
                          target="_blank"
                          css={iconLink}
                        >
                          <img
                            src={getNavigationIcon('whatsappFilled')}
                            alt="whatsapp"
                            loading="lazy"
                          />
                        </a>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </li>
          </ul>
        )}
      </NavigationContainer>
    </Container>
  )
}

const logoImgStyle = css`
  width: 100%;
  height: 100%;
  cursor: pointer;
  object-fit: contain;
`

export default memo(Navbar)
